import React from 'react';
import Markdown from 'react-markdown';
import { renderToStaticMarkup } from 'react-dom/server';
import { answerFormatter } from '@classes/AnswerFormatter';
import { formatNumber } from '@/utils/formatters';

export const TEMPLATE_PREVIEW_ANSWER_CLASS = 'template-preview-answer';
export const TEMPLATE_PREVIEW_PLACEHOLDER_CLASS = 'template-preview-placeholder';
const TEMPLATE_PLACEHOLDER_PATTERN = /^\{\{TEMPLATE_[A-Z0-9_]+\}\}$/;
const TEMPLATE_PLACEHOLDER_GLOBAL_PATTERN = /\{\{TEMPLATE_[A-Z0-9_]+\}\}/g;
const TEMPLATE_PLACEHOLDER_FLEX_GLOBAL_PATTERN = /\{\{\s*(TEMPLATE_[A-Z0-9_]+)\s*\}\}/gi;

function tryParseJsonString(value) {
  if (typeof value !== 'string') {
    return value;
  }

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
}

function jsonAnswerField(parsedValue, fieldName) {
  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    return undefined;
  }
  if (!(fieldName in parsedValue)) {
    return undefined;
  }
  const fieldValue = parsedValue[fieldName];
  if (fieldValue === undefined || fieldValue === null) {
    return '';
  }
  return String(fieldValue);
}

export function extractAnswerText(value) {
  const parsedValue = tryParseJsonString(value);

  if (typeof parsedValue === 'string') {
    return parsedValue;
  }

  if (typeof parsedValue === 'number' || typeof parsedValue === 'boolean') {
    return String(parsedValue);
  }

  if (!parsedValue || typeof parsedValue !== 'object') {
    return '';
  }

  const fromAnswer = jsonAnswerField(parsedValue, 'Answer');
  if (fromAnswer !== undefined) {
    return fromAnswer;
  }
  const fromLowerAnswer = jsonAnswerField(parsedValue, 'answer');
  if (fromLowerAnswer !== undefined) {
    return fromLowerAnswer;
  }
  const fromInitial = jsonAnswerField(parsedValue, 'Initial Answer');
  if (fromInitial !== undefined) {
    return fromInitial;
  }
  const fromSnake = jsonAnswerField(parsedValue, 'initial_answer');
  if (fromSnake !== undefined) {
    return fromSnake;
  }
  const fromCamel = jsonAnswerField(parsedValue, 'initialAnswer');
  if (fromCamel !== undefined) {
    return fromCamel;
  }

  return '';
}

export function normalizeTemplateQueryMetadata(templateQueries) {
  if (!templateQueries) {
    return [];
  }

  if (Array.isArray(templateQueries)) {
    return templateQueries;
  }

  const normalizedTemplateQueries = Object.values(templateQueries);
  return normalizedTemplateQueries;
}

export function generateTemplateKeyLookup(queryListMetadata) {
  const keyLookup = (queryListMetadata || []).reduce((lookup, queryMetadata, index) => {
    lookup[index] = queryMetadata?.template_key || `Question ${index + 1}`;
    return lookup;
  }, {});
  return keyLookup;
}

export function parseRfpStreamMessage(payload) {
  const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
  const id = data?.question_index;
  const rawMessage = data?.answer ?? '';
  return {
    id,
    rawMessage,
    message: extractAnswerText(rawMessage),
    finalAnswer: data?.final_answer === true,
  };
}

export function createAnswersLookupFromPayload(answers, keyLookup) {
  const answersLookup = {};
  (answers || []).forEach((row) => {
    if (!row) return;
    const templateKey = row.template_key || keyLookup?.[row.question_index];
    if (!templateKey) return;
    const existing = answersLookup[templateKey];
    if (existing && !row.final_answer) return;
    answersLookup[templateKey] = extractAnswerText(row.answer);
  });
  return answersLookup;
}

export function createAnswerEntries(queryListMetadata, answersLookup) {
  const answerEntries = (queryListMetadata || []).map((queryMetadata, index) => {
    const templateKey = queryMetadata?.template_key || `Question ${index + 1}`;
    return {
      id: `${templateKey}-${index}`,
      templateKey,
      answer: answersLookup?.[templateKey] || '',
    };
  });

  return answerEntries;
}

function normalizeTemplateText(value) {
  if (typeof value !== 'string') {
    return '';
  }

  return value
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatLargeTemplateNumber(value) {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numericValue) || Math.abs(numericValue) < 100000) {
    return null;
  }

  const stringValue = String(value);
  const decimalPart = stringValue.split('.')[1] || '';
  const fractionDigits = decimalPart.length;

  return formatNumber(numericValue, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

function normalizeTemplateAnswerValue(value) {
  const extractedValue = extractAnswerText(value);

  if (typeof extractedValue === 'string') {
    return formatLargeTemplateNumber(extractedValue) || extractedValue;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return formatLargeTemplateNumber(value) || String(value);
  }

  return '';
}

export function getNestedAnswerEntries(answer) {
  if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
    return [];
  }

  const nestedAnswerEntries = Object.entries(answer).reduce((entries, [templateKey, nestedAnswer]) => {
    const normalizedTemplateKey = normalizeTemplateText(templateKey);

    if (!normalizedTemplateKey) {
      return entries;
    }

    entries.push({
      templateKey: normalizedTemplateKey,
      answer: normalizeTemplateAnswerValue(nestedAnswer),
    });

    return entries;
  }, []);

  return nestedAnswerEntries;
}

export function createTemplateAnswerLookup(answerEntries) {
  const answersLookup = (answerEntries || []).reduce((lookup, answerEntry) => {
    const answer = answerEntry?.answer;

    if (answer && typeof answer === 'object' && !Array.isArray(answer)) {
      getNestedAnswerEntries(answer).forEach(({ templateKey, answer: nestedAnswer }) => {
        lookup[templateKey] = nestedAnswer;
      });
      return lookup;
    }

    const normalizedTemplateKey = normalizeTemplateText(answerEntry?.templateKey);

    if (normalizedTemplateKey) {
      lookup[normalizedTemplateKey] = normalizeTemplateAnswerValue(answer);
    }

    return lookup;
  }, {});

  return answersLookup;
}

function buildExpandedTemplateAnswersLookup(answersLookup) {
  const expanded = { ...answersLookup };
  Object.entries(answersLookup).forEach(([key, value]) => {
    const norm = normalizeTemplateText(key);
    expanded[norm] = value;
    const m = norm.match(/^\{\{\s*(TEMPLATE_[A-Z0-9_]+)\s*\}\}$/i);
    if (m) {
      const canonical = `{{${m[1].toUpperCase()}}}`;
      expanded[canonical] = value;
      expanded[m[1].toUpperCase()] = value;
    }
  });
  return expanded;
}

function resolveAnswerForPlaceholderNormalizedText(expandedLookup, normalizedText) {
  if (Object.prototype.hasOwnProperty.call(expandedLookup, normalizedText)) {
    return expandedLookup[normalizedText];
  }
  const m = normalizedText.match(/^\{\{\s*(TEMPLATE_[A-Z0-9_]+)\s*\}\}$/i);
  if (m) {
    const lookupKey = `{{${m[1].toUpperCase()}}}`;
    if (Object.prototype.hasOwnProperty.call(expandedLookup, lookupKey)) {
      return expandedLookup[lookupKey];
    }
  }
  return undefined;
}

function replaceFlexiblePlaceholdersInTextNodes(doc, expandedLookup) {
  const textNodes = [];
  const walker = doc.createTreeWalker(doc.body, window.NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const parentElement = textNode.parentElement;
    if (
      !parentElement ||
      parentElement.closest(`.${TEMPLATE_PREVIEW_ANSWER_CLASS}`) ||
      parentElement.closest(`.${TEMPLATE_PREVIEW_PLACEHOLDER_CLASS}`)
    ) {
      return;
    }

    const textContent = textNode.textContent || '';
    if (!textContent || !/\{\{\s*TEMPLATE_/i.test(textContent)) {
      return;
    }

    let remaining = textContent;
    const fragment = doc.createDocumentFragment();
    let changed = false;

    while (remaining.length > 0) {
      TEMPLATE_PLACEHOLDER_FLEX_GLOBAL_PATTERN.lastIndex = 0;
      const match = TEMPLATE_PLACEHOLDER_FLEX_GLOBAL_PATTERN.exec(remaining);
      if (!match) {
        fragment.appendChild(doc.createTextNode(remaining));
        break;
      }

      const matchIndex = match.index;
      const rawFull = match[0];
      const lookupKey = `{{${match[1].toUpperCase()}}}`;

      if (matchIndex > 0) {
        fragment.appendChild(doc.createTextNode(remaining.slice(0, matchIndex)));
      }

      if (!Object.prototype.hasOwnProperty.call(expandedLookup, lookupKey)) {
        fragment.appendChild(doc.createTextNode(rawFull));
        remaining = remaining.slice(matchIndex + rawFull.length);
        continue;
      }

      changed = true;
      const answer = expandedLookup[lookupKey];
      const wrapper = doc.createElement('span');
      replaceElementContentsWithAnswer(doc, wrapper, answer);
      fragment.appendChild(wrapper);
      remaining = remaining.slice(matchIndex + rawFull.length);
    }

    if (changed) {
      textNode.replaceWith(fragment);
    }
  });
}

function renderMarkdownAnswerToHtml(answer) {
  const formattedAnswer = answerFormatter.formatResult(String(answer || ''));
  const plugins = answerFormatter.getPluginsForResult(formattedAnswer);

  return renderToStaticMarkup(<Markdown rehypePlugins={plugins}>{formattedAnswer}</Markdown>);
}

function parsePixelValue(value) {
  const parsedValue = parseFloat(value || '0');
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function getElementIndentation(element) {
  if (!element?.style) {
    return 0;
  }

  const marginLeft = parsePixelValue(element.style.marginLeft);
  const paddingLeft = parsePixelValue(element.style.paddingLeft);
  const textIndent = Math.max(0, parsePixelValue(element.style.textIndent));

  return marginLeft + paddingLeft + textIndent;
}

function applyTemplateAnswerIndentation(targetElement, sourceElement) {
  if (!targetElement || !sourceElement) {
    return;
  }

  const indentation = getElementIndentation(sourceElement);
  if (indentation > 0) {
    targetElement.style.paddingLeft = `${indentation}px`;
  } else {
    targetElement.style.removeProperty('padding-left');
  }
}

function replaceElementContentsWithAnswer(doc, element, answer) {
  const renderedAnswerHtml = renderMarkdownAnswerToHtml(answer);
  const container = doc.createElement('div');
  container.innerHTML = renderedAnswerHtml;
  const replacementNodes = Array.from(container.childNodes);

  if (replacementNodes.length === 0) {
    element.classList.add(TEMPLATE_PREVIEW_ANSWER_CLASS);
    applyTemplateAnswerIndentation(element, element);
    element.replaceChildren();
    return;
  }

  if (replacementNodes.length === 1 && replacementNodes[0].nodeName === 'P') {
    element.classList.add(TEMPLATE_PREVIEW_ANSWER_CLASS);
    applyTemplateAnswerIndentation(element, element);
    element.replaceChildren(...Array.from(replacementNodes[0].childNodes).map((node) => node.cloneNode(true)));
    return;
  }

  if (element.tagName === 'P') {
    const wrapper = doc.createElement('div');
    wrapper.classList.add(TEMPLATE_PREVIEW_ANSWER_CLASS);
    applyTemplateAnswerIndentation(wrapper, element);
    replacementNodes.forEach((node) => {
      wrapper.appendChild(node.cloneNode(true));
    });
    element.replaceWith(wrapper);
    return;
  }

  element.classList.add(TEMPLATE_PREVIEW_ANSWER_CLASS);
  applyTemplateAnswerIndentation(element, element);
  element.replaceChildren(...replacementNodes.map((node) => node.cloneNode(true)));
}

function isTemplatePlaceholderText(text) {
  return TEMPLATE_PLACEHOLDER_PATTERN.test(normalizeTemplateText(text));
}

function hasFilledTemplateAnswer(element) {
  return (
    element.classList.contains(TEMPLATE_PREVIEW_ANSWER_CLASS) ||
    !!element.querySelector(`.${TEMPLATE_PREVIEW_ANSWER_CLASS}`)
  );
}

function isPlaceholderOnlyCell(cell) {
  if (hasFilledTemplateAnswer(cell)) {
    return false;
  }

  const normalizedCellText = normalizeTemplateText(cell.textContent);
  if (!normalizedCellText) {
    return false;
  }

  return isTemplatePlaceholderText(normalizedCellText);
}

function removeUnfilledTemplateRows(doc) {
  const rows = Array.from(doc.body.querySelectorAll('tr'));

  rows.forEach((row) => {
    const bodyCells = Array.from(row.querySelectorAll('td'));
    if (bodyCells.length === 0) {
      return;
    }

    const hasMergedCells = bodyCells.some((cell) => cell.rowSpan > 1 || cell.colSpan > 1);
    if (hasMergedCells) {
      return;
    }

    if (bodyCells.some((cell) => hasFilledTemplateAnswer(cell))) {
      return;
    }

    const nonEmptyBodyCells = bodyCells.filter((cell) => normalizeTemplateText(cell.textContent));
    if (nonEmptyBodyCells.length === 0) {
      return;
    }

    const isPlaceholderOnlyRow = nonEmptyBodyCells.every((cell) => isPlaceholderOnlyCell(cell));
    if (!isPlaceholderOnlyRow) {
      return;
    }

    row.remove();
  });
}

function wrapUnresolvedTemplatePlaceholders(doc) {
  const textNodeWalker = doc.createTreeWalker(doc.body, window.NodeFilter.SHOW_TEXT);
  const textNodes = [];

  while (textNodeWalker.nextNode()) {
    textNodes.push(textNodeWalker.currentNode);
  }

  textNodes.forEach((textNode) => {
    const parentElement = textNode.parentElement;
    const textContent = textNode.textContent || '';

    if (
      !parentElement ||
      !textContent ||
      parentElement.closest(`.${TEMPLATE_PREVIEW_ANSWER_CLASS}`) ||
      parentElement.closest(`.${TEMPLATE_PREVIEW_PLACEHOLDER_CLASS}`)
    ) {
      return;
    }

    const matches = Array.from(textContent.matchAll(TEMPLATE_PLACEHOLDER_GLOBAL_PATTERN));
    if (matches.length === 0) {
      return;
    }

    const fragment = doc.createDocumentFragment();
    let currentIndex = 0;

    matches.forEach((match) => {
      const matchText = match[0];
      const matchIndex = match.index || 0;

      if (matchIndex > currentIndex) {
        fragment.appendChild(doc.createTextNode(textContent.slice(currentIndex, matchIndex)));
      }

      const placeholderWrapper = doc.createElement('span');
      placeholderWrapper.classList.add(TEMPLATE_PREVIEW_PLACEHOLDER_CLASS);
      placeholderWrapper.textContent = matchText;
      fragment.appendChild(placeholderWrapper);

      currentIndex = matchIndex + matchText.length;
    });

    if (currentIndex < textContent.length) {
      fragment.appendChild(doc.createTextNode(textContent.slice(currentIndex)));
    }

    textNode.replaceWith(fragment);
  });
}

export function replaceTemplateKeysWithAnswers(html, answerEntries) {
  if (!html || typeof window === 'undefined') {
    return html || '';
  }

  const answersLookup = createTemplateAnswerLookup(answerEntries);
  const expandedLookup = buildExpandedTemplateAnswersLookup(answersLookup);
  const hasAnswers = Object.keys(answersLookup).length > 0;

  const parser = new window.DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const elements = Array.from(doc.body.querySelectorAll('*'));

  if (hasAnswers) {
    elements.forEach((element) => {
      const normalizedElementText = normalizeTemplateText(element.textContent);
      const matchingAnswer = resolveAnswerForPlaceholderNormalizedText(expandedLookup, normalizedElementText);

      if (matchingAnswer === undefined) {
        return;
      }

      replaceElementContentsWithAnswer(doc, element, matchingAnswer);
    });

    replaceFlexiblePlaceholdersInTextNodes(doc, expandedLookup);

    removeUnfilledTemplateRows(doc);
  }

  wrapUnresolvedTemplatePlaceholders(doc);

  return doc.body.innerHTML;
}
